// OVERTIME ANALYSIS REPORT
// Overtime trends, patterns, and employee analysis.
// Phase 7C: Advanced Analytics

import React, {useState, useEffect} from 'react'
import {Row, Col, Card, Table, Badge, Spinner, Alert} from 'react-bootstrap'
import {toast} from 'react-toastify'
import {getTeamUtilizationReport} from '../../../../api/ReportsAPI'
import ExportButton from './ExportButton'

interface OvertimeAnalysisProps {
  managerId: string
  startDate: string
  endDate: string
}

interface EmployeeOvertime {
  userId: string
  employeeCode: string
  name: string
  overtimeHours: number
  totalHours: number
  overtimePercentage: number
  entriesCount: number
}

interface OvertimeData {
  totalOvertimeHours: number
  affectedEmployees: number
  averageOvertimePerEmployee: number
  highestOvertimeEmployee: string
  employees: EmployeeOvertime[]
}

const OvertimeAnalysis: React.FC<OvertimeAnalysisProps> = ({
  managerId,
  startDate,
  endDate,
}) => {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<OvertimeData | null>(null)

  useEffect(() => {
    if (managerId && startDate && endDate) {
      fetchReport()
    }
  }, [managerId, startDate, endDate])

  const fetchReport = async () => {
    setLoading(true)
    try {
      // Use team utilization API to get overtime data
      const response = await getTeamUtilizationReport({
        managerId,
        startDate,
        endDate,
        includeLeave: false,
      })

      if (response.data?.statusCode === 200) {
        const reportData = response.data.data

        // Calculate overtime statistics
        const employees: EmployeeOvertime[] = reportData.byEmployee
          .map((emp: any) => ({
            userId: emp.userId,
            employeeCode: emp.employeeCode,
            name: emp.name,
            overtimeHours: emp.overtimeHours,
            totalHours: emp.actualHours,
            overtimePercentage:
              emp.actualHours > 0 ? (emp.overtimeHours / emp.actualHours) * 100 : 0,
            entriesCount: emp.entriesCount,
          }))
          .sort((a: EmployeeOvertime, b: EmployeeOvertime) => b.overtimeHours - a.overtimeHours)

        const totalOvertimeHours = employees.reduce((sum, emp) => sum + emp.overtimeHours, 0)
        const affectedEmployees = employees.filter((emp) => emp.overtimeHours > 0).length
        const averageOvertimePerEmployee =
          affectedEmployees > 0 ? totalOvertimeHours / affectedEmployees : 0
        const highestOvertimeEmployee =
          employees.length > 0 && employees[0].overtimeHours > 0 ? employees[0].name : 'None'

        setData({
          totalOvertimeHours,
          affectedEmployees,
          averageOvertimePerEmployee,
          highestOvertimeEmployee,
          employees,
        })
      } else {
        toast.error('Failed to load overtime analysis')
      }
    } catch (error: any) {
      console.error('Error fetching overtime analysis:', error)
      toast.error(error?.response?.data?.message || 'Failed to load report')
    } finally {
      setLoading(false)
    }
  }

  // Utility function to get overtime severity color
  const getOvertimeColor = (percentage: number): string => {
    if (percentage === 0) return 'success'
    if (percentage < 10) return 'info'
    if (percentage < 20) return 'warning'
    return 'danger'
  }

  const getOvertimeBadgeText = (percentage: number): string => {
    if (percentage === 0) return 'No OT'
    if (percentage < 10) return 'Low'
    if (percentage < 20) return 'Moderate'
    return 'High'
  }

  // Render loading state
  if (loading) {
    return (
      <div className='text-center py-5'>
        <Spinner animation='border' variant='primary' />
        <p className='text-muted mt-3'>Loading overtime analysis...</p>
      </div>
    )
  }

  // Render empty state
  if (!data) {
    return (
      <div className='text-center py-5'>
        <i className='fas fa-clock fa-3x text-muted mb-3'></i>
        <p className='text-muted'>No overtime data available for selected period</p>
      </div>
    )
  }

  const overtimeAlert =
    data.totalOvertimeHours > 0 && data.affectedEmployees > data.employees.length / 2

  return (
    <>
      {/* Export Button */}
      <Row className='mb-3'>
        <Col className='d-flex justify-content-end'>
          <ExportButton
            reportType='team-utilization'
            filters={{
              managerId,
              startDate,
              endDate,
            }}
          />
        </Col>
      </Row>

      {/* Alert for High Overtime */}
      {overtimeAlert && (
        <Row className='mb-3'>
          <Col>
            <Alert variant='warning'>
              <i className='fas fa-exclamation-triangle me-2'></i>
              <strong>High Overtime Alert:</strong> Over 50% of your team is working overtime.
              Consider workload redistribution or hiring additional resources.
            </Alert>
          </Col>
        </Row>
      )}

      {/* Summary Cards */}
      <Row className='mb-4'>
        <Col md={3} sm={6} className='mb-3'>
          <Card className='h-100 shadow-sm border-start border-warning border-4'>
            <Card.Body>
              <div className='d-flex justify-content-between align-items-center'>
                <div>
                  <p className='text-muted mb-1 small'>Total Overtime</p>
                  <h3 className='mb-0 text-warning'>{data.totalOvertimeHours.toFixed(1)}h</h3>
                </div>
                <i className='fas fa-clock fa-2x text-warning opacity-50'></i>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3} sm={6} className='mb-3'>
          <Card className='h-100 shadow-sm border-start border-danger border-4'>
            <Card.Body>
              <div className='d-flex justify-content-between align-items-center'>
                <div>
                  <p className='text-muted mb-1 small'>Affected Employees</p>
                  <h3 className='mb-0'>
                    {data.affectedEmployees}/{data.employees.length}
                  </h3>
                  <small className='text-muted'>
                    {((data.affectedEmployees / data.employees.length) * 100).toFixed(0)}% of team
                  </small>
                </div>
                <i className='fas fa-users fa-2x text-danger opacity-50'></i>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3} sm={6} className='mb-3'>
          <Card className='h-100 shadow-sm border-start border-info border-4'>
            <Card.Body>
              <div className='d-flex justify-content-between align-items-center'>
                <div>
                  <p className='text-muted mb-1 small'>Avg OT per Employee</p>
                  <h3 className='mb-0'>{data.averageOvertimePerEmployee.toFixed(1)}h</h3>
                  <small className='text-muted'>Among affected employees</small>
                </div>
                <i className='fas fa-chart-bar fa-2x text-info opacity-50'></i>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col md={3} sm={6} className='mb-3'>
          <Card className='h-100 shadow-sm border-start border-primary border-4'>
            <Card.Body>
              <div className='d-flex justify-content-between align-items-center'>
                <div>
                  <p className='text-muted mb-1 small'>Highest OT Employee</p>
                  <h6 className='mb-0 text-truncate' title={data.highestOvertimeEmployee}>
                    {data.highestOvertimeEmployee}
                  </h6>
                  {data.employees.length > 0 && data.employees[0].overtimeHours > 0 && (
                    <small className='text-muted'>
                      {data.employees[0].overtimeHours.toFixed(1)}h overtime
                    </small>
                  )}
                </div>
                <i className='fas fa-trophy fa-2x text-primary opacity-50'></i>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Overtime Distribution Chart */}
      <Row className='mb-4'>
        <Col>
          <Card className='shadow-sm'>
            <Card.Header>
              <h5 className='mb-0'>
                <i className='fas fa-chart-bar me-2'></i>
                Overtime Distribution by Employee
              </h5>
            </Card.Header>
            <Card.Body>
              {data.employees.filter((e) => e.overtimeHours > 0).length > 0 ? (
                <div style={{overflowX: 'auto'}}>
                  <div style={{minWidth: '600px'}}>
                    <div className='d-flex align-items-end' style={{height: '250px', gap: '8px'}}>
                      {data.employees
                        .filter((emp) => emp.overtimeHours > 0)
                        .map((emp, index) => {
                          const maxOT = Math.max(
                            ...data.employees.map((e) => e.overtimeHours)
                          )
                          const height = (emp.overtimeHours / maxOT) * 200

                          return (
                            <div
                              key={index}
                              className='flex-fill text-center'
                              style={{position: 'relative'}}
                            >
                              <div
                                className={`bg-${getOvertimeColor(emp.overtimePercentage)} rounded`}
                                style={{
                                  height: `${height}px`,
                                  cursor: 'pointer',
                                  minWidth: '40px',
                                }}
                                title={`${emp.name}: ${emp.overtimeHours.toFixed(1)}h (${emp.overtimePercentage.toFixed(1)}%)`}
                              >
                                <div
                                  style={{
                                    position: 'absolute',
                                    top: '-20px',
                                    left: '50%',
                                    transform: 'translateX(-50%)',
                                    fontSize: '11px',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {emp.overtimeHours.toFixed(1)}h
                                </div>
                              </div>
                              <small
                                className='text-muted d-block mt-1'
                                style={{fontSize: '10px', transform: 'rotate(-45deg)'}}
                              >
                                {emp.name.split(' ')[0]}
                              </small>
                            </div>
                          )
                        })}
                    </div>
                  </div>
                </div>
              ) : (
                <p className='text-center text-success py-4'>
                  <i className='fas fa-check-circle fa-2x mb-2'></i>
                  <br />
                  No overtime recorded in this period. Excellent work-life balance!
                </p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Employee-wise Overtime Table */}
      <Row className='mb-4'>
        <Col>
          <Card className='shadow-sm'>
            <Card.Header>
              <h5 className='mb-0'>
                <i className='fas fa-users me-2'></i>
                Employee-wise Overtime Breakdown
              </h5>
            </Card.Header>
            <Card.Body>
              <div className='table-responsive'>
                <Table hover className='mb-0'>
                  <thead className='table-light'>
                    <tr>
                      <th>Employee</th>
                      <th>Code</th>
                      <th className='text-end'>Overtime Hours</th>
                      <th className='text-end'>Total Hours</th>
                      <th className='text-end'>OT Percentage</th>
                      <th className='text-center'>Status</th>
                      <th className='text-center'>Entries</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.employees.map((emp, index) => (
                      <tr key={index}>
                        <td>
                          <strong>{emp.name}</strong>
                        </td>
                        <td>
                          <code>{emp.employeeCode}</code>
                        </td>
                        <td className='text-end'>
                          {emp.overtimeHours > 0 ? (
                            <span className='text-warning fw-bold'>
                              {emp.overtimeHours.toFixed(1)}h
                            </span>
                          ) : (
                            <span className='text-success'>0h</span>
                          )}
                        </td>
                        <td className='text-end'>{emp.totalHours.toFixed(1)}h</td>
                        <td className='text-end'>{emp.overtimePercentage.toFixed(1)}%</td>
                        <td className='text-center'>
                          <Badge bg={getOvertimeColor(emp.overtimePercentage)}>
                            {getOvertimeBadgeText(emp.overtimePercentage)}
                          </Badge>
                        </td>
                        <td className='text-center'>{emp.entriesCount}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className='table-light'>
                    <tr>
                      <td colSpan={2}>
                        <strong>Total</strong>
                      </td>
                      <td className='text-end'>
                        <strong>{data.totalOvertimeHours.toFixed(1)}h</strong>
                      </td>
                      <td className='text-end'>
                        <strong>
                          {data.employees.reduce((sum, e) => sum + e.totalHours, 0).toFixed(1)}h
                        </strong>
                      </td>
                      <td colSpan={3}></td>
                    </tr>
                  </tfoot>
                </Table>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Recommendations */}
      <Row>
        <Col>
          <Card className='shadow-sm border-start border-info border-4'>
            <Card.Body>
              <h5 className='mb-3'>
                <i className='fas fa-lightbulb me-2 text-warning'></i>
                Recommendations
              </h5>
              <Row>
                <Col md={4}>
                  <div className='mb-2'>
                    <strong>Workload Balance:</strong>
                    <p className='mb-0 text-muted'>
                      {data.totalOvertimeHours === 0 ? (
                        <span className='text-success'>✓ Team has excellent work-life balance.</span>
                      ) : data.affectedEmployees <= data.employees.length / 3 ? (
                        <span className='text-info'>
                          Monitor employees with high overtime.
                        </span>
                      ) : (
                        <span className='text-warning'>
                          Consider redistributing workload across team.
                        </span>
                      )}
                    </p>
                  </div>
                </Col>
                <Col md={4}>
                  <div className='mb-2'>
                    <strong>Resource Planning:</strong>
                    <p className='mb-0 text-muted'>
                      {data.totalOvertimeHours > data.employees.length * 5 ? (
                        <span className='text-warning'>
                          High overtime suggests need for additional resources.
                        </span>
                      ) : (
                        <span className='text-success'>✓ Current team size is adequate.</span>
                      )}
                    </p>
                  </div>
                </Col>
                <Col md={4}>
                  <div className='mb-2'>
                    <strong>Action Items:</strong>
                    <p className='mb-0 text-muted'>
                      {data.employees.filter((e) => e.overtimePercentage > 20).length > 0 ? (
                        <span className='text-danger'>
                          Urgent: Meet with employees working {">"} 20% overtime.
                        </span>
                      ) : data.totalOvertimeHours > 0 ? (
                        <span className='text-info'>
                          Review project timelines and resource allocation.
                        </span>
                      ) : (
                        <span className='text-success'>✓ Continue current management practices.</span>
                      )}
                    </p>
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  )
}

export default OvertimeAnalysis






